/* eslint-disable @typescript-eslint/no-namespace */
import { z } from "zod";

import {
  countryCodeWithLowerCaseSchema,
  customPropertiesRequestSchema,
  customPropertiesSchema,
  dateSchema,
  eventPropertiesSchema,
  internalEventPropertiesSchema,
  objectIdSchema,
  recommendationRequestSchema,
} from "../utils/zodSchemas";

import type { Serialize } from "../utils/Base";
import type { RecResponse } from "../utils/zodSchemas";

enum HttpMethod {
  GET = "get",
  POST = "post",
  PUT = "put",
  DELETE = "delete",
}

export enum DevicePlatform {
  ios = "ios",
  android = "android",
  browser = "browser",
}

export enum Gender {
  male = "male",
  female = "female",
}

const applicationsRoot = "/applications/{application_id}";
const applicationsRootV2 = "/v2/applications/{application_id}";

export namespace APIs {
  export namespace bluxUsersInitialize {
    export const name = "bluxUsersInitialize";
    export const path = `${applicationsRoot}/blux-users/initialize`;
    export const method = HttpMethod.POST;

    export const bodySchema = z.object({
      session_id: z.string().optional(),
      device_id: objectIdSchema.optional(),
      country_code: countryCodeWithLowerCaseSchema.optional(),
      device_model: z.string(),
      language_code: z.string(),
      os_version: z.string(),
      platform: z.nativeEnum(DevicePlatform),
      sdk_type: z.string(),
      sdk_version: z.string(),
      timezone: z.string(),
      blux_user_id: z.string().optional(),
    });
    export type Body = Serialize<z.input<typeof bodySchema>>;

    export const pathSchema = z.object({
      application_id: z.string().optional(),
    });
    export type PathParam = Serialize<z.input<typeof pathSchema>>;

    export type ResponseData = {
      blux_user_id: string;
      device_id: string;
    };
  }

  export namespace bluxUsersSignIn {
    export const name = "bluxUsersSignIn";
    export const path = `${applicationsRoot}/blux-users/{blux_user_id}/sign-in`;
    export const method = HttpMethod.PUT;

    export const bodySchema = z.object({
      user_id: z.string(),
      device_id: z.string().optional(), // TODO: @all sdk update 후 필수로 변경
    });
    export type Body = Serialize<z.input<typeof bodySchema>>;

    export const pathSchema = z.object({
      application_id: z.string().optional(),
      blux_user_id: objectIdSchema,
    });
    export type PathParam = Serialize<z.input<typeof pathSchema>>;

    export type ResponseData = {
      blux_user_id: string;
    };
  }

  export namespace bluxUsersSignOut {
    export const name = "bluxUsersSignOut";
    export const path = `${applicationsRoot}/blux-users/{blux_user_id}/sign-out`;
    export const method = HttpMethod.PUT;

    export const bodySchema = z.object({
      device_id: objectIdSchema.optional(),
    });
    export type Body = Serialize<z.input<typeof bodySchema>>;

    export const pathSchema = z.object({
      application_id: z.string().optional(), // TODO: @all objectIdSchema 로 변경
      blux_user_id: objectIdSchema,
    });
    export type PathParam = Serialize<z.input<typeof pathSchema>>;

    export type ResponseData = {
      blux_user_id: string;
    };
  }

  export namespace bluxUsersCollectEventsV2 {
    export const name = "bluxUsersCollectEventsV2";
    export const path = `${applicationsRootV2}/blux-users/{blux_user_id}/collect-events`;
    export const method = HttpMethod.POST;
    export const pathSchema = z.object({
      application_id: objectIdSchema,
      blux_user_id: objectIdSchema,
    });
    export type PathParam = Serialize<z.input<typeof pathSchema>>;

    export const bodySchema = z.object({
      device_id: objectIdSchema,
      events: z.array(
        z.object({
          event_type: z.string(),
          event_properties: eventPropertiesSchema.optional(),
          custom_event_properties: customPropertiesSchema.optional(),
          internal_event_properties: internalEventPropertiesSchema.optional(),
          captured_at: dateSchema,
        }),
      ),
    });
    export type Body = Serialize<z.input<typeof bodySchema>>;

    export type ResponseData = {
      nextPollDelayMs: number;
      inapp?: {
        notificationId: string;
        inappId: string;
        htmlString: string;
        baseUrl: string;
      };
    };
  }

  export namespace bluxUsersUpdateProperties {
    export const name = "bluxUsersUpdateProperties";
    export const path = `${applicationsRoot}/blux-users/{blux_user_id}/update-properties`;
    export const method = HttpMethod.PUT;

    export const bodySchema = z.object({
      user_properties: z
        .object({
          phone_number: z.string().nullish(),
          email_address: z.string().nullish(),
          nighttime_notification_consent: z.boolean().nullish(),
          marketing_notification_consent: z.boolean().nullish(),
          marketing_notification_sms_consent: z.boolean().nullish(),
          marketing_notification_email_consent: z.boolean().nullish(),
          marketing_notification_kakao_consent: z.boolean().nullish(),
          marketing_notification_push_consent: z.boolean().nullish(),
          is_all_notification_blocked: z.boolean().nullish(),

          gender: z.nativeEnum(Gender).nullish(),
          age: z.number().nullish(),
        })
        .optional(),
      custom_user_properties: customPropertiesRequestSchema.optional(),
    });
    export type Body = Serialize<z.input<typeof bodySchema>>;

    export const pathSchema = z.object({
      application_id: z.string(),
      blux_user_id: objectIdSchema,
    });
    export type PathParam = Serialize<z.input<typeof pathSchema>>;

    export type ResponseData = {
      blux_user_id: string;
    };
  }

  export namespace bluxUsersGetItemRecommendationsV2 {
    export const name = "bluxUsersGetItemRecommendationsV2";
    export const path = `${applicationsRootV2}/blux-users/{blux_user_id}/item-recommendations`;
    export const method = HttpMethod.POST;

    export const bodySchema = recommendationRequestSchema;
    export type Body = Serialize<z.input<typeof bodySchema>>;

    export const pathSchema = z.object({
      application_id: objectIdSchema,
      blux_user_id: objectIdSchema,
    });
    export type PathParam = Serialize<z.input<typeof pathSchema>>;

    export type ResponseData = RecResponse;
  }

  export namespace crmEventsCreate {
    export const name = "crmEventsCreate";
    export const path = `${applicationsRoot}/crm-events`;
    export const method = HttpMethod.POST;

    export const bodySchema = z.object({
      notification_id: objectIdSchema,
      crm_event_type: z.union([
        z.literal("push_opened"),
        z.literal("button_clicked"),
        z.literal("landing_clicked"),
        z.literal("inapp_opened"),
      ]),
      captured_at: dateSchema,
      item_id: z.string().optional(),
    });
    export type Body = Serialize<z.input<typeof bodySchema>>;

    export const pathSchema = z.object({
      application_id: z.string(),
    });
    export type PathParam = Serialize<z.input<typeof pathSchema>>;

    export type ResponseData = boolean;
  }

  export namespace notificationsUpdate {
    export const name = "notificationsUpdate";
    export const path = `${applicationsRoot}/notifications/{notification_id}`;
    export const method = HttpMethod.POST;

    export const pathSchema = z.object({
      application_id: objectIdSchema,
      notification_id: objectIdSchema,
    });
    export type PathParam = Serialize<z.input<typeof pathSchema>>;

    export const bodySchema = z.object({
      status: z.literal("received"),
    });
    export type Body = Serialize<z.input<typeof bodySchema>>;
    export type ResponseData = boolean;
  }
}
